import { useState } from "react";
import { Link } from "wouter";
import { helpCategories, getIconMapping, searchArticles } from "./help/content";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import Navigation from "@/components/Navigation";
import Footer from "@/components/ui/footer";
import {
  Book,
  Heart,
  Wand2,
  Volume2,
  Download,
  ShoppingBag,
  Star,
  Check,
  ArrowRight,
  Play,
  Users,
  Sparkles,
  Clock,
  Shield,
  Gift,
  Info,
  Search,
  HelpCircle,
  MessageCircle,
  Phone,
  Mail,
  ChevronDown,
  ChevronUp,
  FileText,
  BookOpen,
  Settings,
  CreditCard,
  User,
  Lock,
  Zap,
  Brain,
  Image,
  Printer,
} from "lucide-react";
import { TeddyBear } from "@/components/icons/TeddyBear";
import { motion, AnimatePresence } from "framer-motion";

// Categories and articles are now imported from content registry

// Popular articles are now derived from content registry

const contactOptions = [
  {
    title: "Email Support",
    description: "Send us a detailed message and we'll respond within 24 hours",
    icon: Mail,
    availability: "Response within 24h",
    action: "Send Email",
    href: "/contact",
  },
];

const mobileApps = [
  {
    platform: "iOS",
    icon: "/applestore.png",
    title: "SnuggleCrafters for iPhone & iPad",
    description:
      "Native iOS app with offline reading, push notifications, and seamless sync",
    features: [
      "Offline story reading",
      "Push notifications for new stories",
      "iCloud sync across devices",
      "Apple Watch companion app",
      "Siri shortcuts integration",
    ],
    status: "Coming Soon",
    releaseDate: "Q4 2025",
    appStoreUrl: "#",
    previewImage: "/app-store-ios-preview.png",
  },
  {
    platform: "Android",
    icon: "/GoogleStore.png",
    title: "SnuggleCrafters for Android",
    description:
      "Full-featured Android app with Google Play integration and offline capabilities",
    features: [
      "Offline story access",
      "Google Play integration",
      "Material Design UI",
      "Android Auto support",
      "Google Assistant integration",
    ],
    status: "Coming Soon",
    releaseDate: "Q4 2025",
    playStoreUrl: "#",
    previewImage: "/play-store-android-preview.png",
  },
];

const faqData = [
  {
    category: "General",
    questions: [
      {
        question: "What is SnuggleCrafters.com?",
        answer:
          "SnuggleCrafters.com is an AI-powered platform that creates personalized bedtime stories for children. Our advanced AI technology crafts unique stories featuring your child as the main character, incorporating their interests, favorite themes, and personal details to create magical, engaging bedtime experiences.",
      },
      {
        question: "How does the AI story generation work?",
        answer:
          "Our AI analyzes your child's profile, interests, and preferences to create completely unique stories. The system uses advanced natural language processing to craft age-appropriate narratives that feature your child as the protagonist, ensuring each story is one-of-a-kind and perfectly tailored to your little one.",
      },
      {
        question: "Is SnuggleCrafters.com safe for children?",
        answer:
          "Absolutely! We prioritize child safety and privacy. All content is age-appropriate, and we have strict content filters. We're COPPA compliant and never share children's personal information. Parents have full control over their child's profile and can review all generated content.",
      },
    ],
  },
  {
    category: "Mobile Apps",
    questions: [
      {
        question: "When will the mobile apps be available?",
        answer:
          "We're excited to announce that our native iOS and Android apps are coming in Q4 2025! The apps will feature offline reading, push notifications, seamless sync, and native mobile optimizations for the best storytelling experience on your phone or tablet.",
      },
      {
        question: "What features will the mobile apps have?",
        answer:
          "Our mobile apps will include offline story reading, push notifications for new stories, seamless cloud sync, native performance optimizations, and platform-specific integrations like Siri shortcuts on iOS and Google Assistant on Android.",
      },
      {
        question: "Will I need to pay separately for the mobile apps?",
        answer:
          "No! Your existing SnuggleCrafters.com subscription will work seamlessly across all platforms - web, iOS, and Android. One subscription gives you access everywhere.",
      },
    ],
  },
  {
    category: "Subscription & Billing",
    questions: [
      {
        question: "What subscription plans do you offer?",
        answer:
          "We offer flexible monthly and yearly plans, plus a free trial to get you started. Our plans include unlimited story generation, offline access, and premium features. Check our pricing page for current rates and special offers.",
      },
      {
        question: "Can I cancel my subscription anytime?",
        answer:
          "Yes, you can cancel your subscription at any time from your account settings. Your access will continue until the end of your current billing period, and you can reactivate anytime.",
      },
      {
        question: "Do you offer gift subscriptions?",
        answer:
          "Absolutely! Gift subscriptions are perfect for birthdays, holidays, or any special occasion. You can purchase gift subscriptions for 1, 3, 6, or 12 months, and the recipient will receive a beautiful digital gift card.",
      },
    ],
  },
  {
    category: "Technical",
    questions: [
      {
        question: "What devices and browsers are supported?",
        answer:
          "SnuggleCrafters.com works on all modern browsers (Chrome, Safari, Firefox, Edge) and devices (desktop, tablet, mobile). We also have native iOS and Android apps coming in Q4 2025 for the best mobile experience.",
      },
      {
        question: "Can I read stories offline?",
        answer:
          "Yes! You can download stories for offline reading on our web platform, and our upcoming mobile apps will have full offline capabilities, so you can enjoy stories anywhere, even without internet connection.",
      },
      {
        question: "How do I troubleshoot audio issues?",
        answer:
          "If you're experiencing audio problems, try refreshing the page, checking your browser's audio settings, or clearing your browser cache. For persistent issues, contact our support team for personalized assistance.",
      },
    ],
  },
];

// Navigation items moved to main Navigation component

export default function HelpPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCategory, setExpandedCategory] = useState<number | null>(null);
  const [expandedFaq, setExpandedFaq] = useState<{
    category: number;
    question: number;
  } | null>(null);

  const toggleCategory = (index: number) => {
    setExpandedCategory(expandedCategory === index ? null : index);
  };

  const toggleFaq = (categoryIndex: number, questionIndex: number) => {
    const key = { category: categoryIndex, question: questionIndex };
    setExpandedFaq(
      expandedFaq?.category === categoryIndex &&
        expandedFaq?.question === questionIndex
        ? null
        : key,
    );
  };

  const filteredArticles = searchQuery ? searchArticles(searchQuery) : [];

  // Popular articles from content registry
  const popularArticles = [
    {
      title: "Getting Started with SnuggleCrafters",
      description:
        "Learn the basics of creating personalized bedtime stories for your child",
      category: "Getting Started",
      views: "2.1k",
    },
    {
      title: "Story Customization Guide",
      description:
        "Complete guide to personalizing stories with your child's preferences",
      category: "Stories",
      views: "1.8k",
    },
    {
      title: "Account & Subscription Management",
      description:
        "How to manage your subscription, billing, and account settings",
      category: "Account",
      views: "1.5k",
    },
    {
      title: "Mobile Apps Coming Q4 2025",
      description:
        "Everything you need to know about our upcoming mobile applications",
      category: "Mobile Apps",
      views: "1.2k",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50">
      {/* Navigation */}
      <Navigation />

      {/* Hero Section */}
      <section className="py-12 sm:py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Badge className="mb-4 bg-gradient-to-r from-purple-600 to-teal-600 text-white text-sm">
                🆘 Help Center
              </Badge>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-purple-600 via-teal-600 to-amber-600 bg-clip-text text-transparent leading-tight">
                How Can We Help You?
              </h1>
              <p className="text-lg sm:text-xl text-gray-700 mb-6 sm:mb-8 leading-relaxed max-w-4xl mx-auto">
                Find answers to common questions, learn how to use our features,
                and get support when you need it.
              </p>

              {/* Search Bar */}
              <div className="max-w-2xl mx-auto mb-8">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    type="text"
                    placeholder="Search for help articles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-3 text-lg"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Search Results */}
      {searchQuery && (
        <section className="py-8 px-4 bg-white">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-2xl font-bold mb-6">
              Search Results ({filteredArticles.length} found)
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article, index) => {
                // Find the category this article belongs to
                const category = helpCategories.find((cat) =>
                  cat.articles.some(
                    (catArticle) => catArticle.slug === article.slug,
                  ),
                );

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <Link
                      href={`/help/${category?.slug}/${article.slug}`}
                      data-testid={`link-article-${article.slug}`}
                      className="block h-full"
                    >
                      <Card className="h-full hover:shadow-lg transition-shadow duration-300 cursor-pointer">
                        <CardContent className="p-6">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="secondary">
                              {category?.title || "General"}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {article.type === "guide"
                                ? "Guide"
                                : "Troubleshooting"}
                            </Badge>
                          </div>
                          <h3 className="font-semibold text-lg mb-2">
                            {article.title}
                          </h3>
                          <p className="text-gray-600 text-sm">
                            {article.description}
                          </p>
                          <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                            <span>Difficulty: {article.difficulty}</span>
                            <span>•</span>
                            <span>{article.estimatedTime}</span>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Help Categories */}
      <section className="py-12 sm:py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
              Browse by Category
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Find help organized by topic and feature
            </p>
          </div>

          <div className="space-y-4">
            {helpCategories.map((category, index) => {
              const Icon = getIconMapping(category.iconKey);
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card>
                    <CardContent className="p-0">
                      <button
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                        onClick={() => toggleCategory(index)}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-teal-600 rounded-lg flex items-center justify-center">
                            <Icon className="h-5 w-5 text-white" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">
                              {category.title}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {category.articles.length} articles
                            </p>
                          </div>
                        </div>
                        {expandedCategory === index ? (
                          <ChevronUp className="h-5 w-5 text-gray-500" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-500" />
                        )}
                      </button>
                      <AnimatePresence>
                        {expandedCategory === index && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="px-6 pb-4 border-t border-gray-100">
                              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                                {category.articles.map(
                                  (article, articleIndex) => (
                                    <Link
                                      key={articleIndex}
                                      href={`/help/${category.slug}/${article.slug}`}
                                      data-testid={`link-article-${article.slug}`}
                                      className="block p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                                    >
                                      <div className="flex items-center gap-2 mb-2">
                                        <Badge
                                          variant="outline"
                                          className="text-xs"
                                        >
                                          {article.type === "guide"
                                            ? "Guide"
                                            : "Troubleshooting"}
                                        </Badge>
                                        <Badge
                                          variant="secondary"
                                          className={`text-xs ${
                                            article.difficulty === "beginner"
                                              ? "bg-green-100 text-green-800"
                                              : article.difficulty ===
                                                  "intermediate"
                                                ? "bg-yellow-100 text-yellow-800"
                                                : "bg-red-100 text-red-800"
                                          }`}
                                        >
                                          {article.difficulty}
                                        </Badge>
                                        <span className="text-xs text-gray-500">
                                          {article.estimatedTime}
                                        </span>
                                      </div>
                                      <h4 className="font-medium text-sm mb-1">
                                        {article.title}
                                      </h4>
                                      <p className="text-xs text-gray-600">
                                        {article.description}
                                      </p>
                                    </Link>
                                  ),
                                )}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Popular Articles */}
      <section className="py-12 sm:py-20 px-4 bg-gradient-to-br from-purple-50 to-teal-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
              Popular Articles
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Most viewed help articles from our community
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {popularArticles.map((article, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="secondary" className="text-xs">
                        {article.category}
                      </Badge>
                      <span className="text-xs text-gray-500">
                        {article.views} views
                      </span>
                    </div>
                    <h3 className="font-semibold text-lg mb-2">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {article.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile Apps Coming Soon */}
      <section className="py-12 sm:py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
              Mobile Apps Coming Soon
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Native iOS and Android apps with enhanced features and offline
              capabilities
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-8">
            {mobileApps.map((app, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <Card className="h-full hover:shadow-xl transition-shadow duration-300 border-2 border-gray-100">
                  <CardContent className="p-8">
                    <div className="text-center mb-6">
                      <div className="flex justify-center mb-4">
                        <img
                          src={app.icon}
                          alt={`${app.platform} App Store`}
                          className="w-60 h-60 object-contain"
                        />
                      </div>
                      <h3 className="text-2xl font-bold mb-2">{app.title}</h3>
                      <p className="text-gray-600 mb-4">{app.description}</p>
                      <div className="flex items-center justify-center gap-2 mb-4">
                        <Badge className="bg-gradient-to-r from-purple-600 to-teal-600 text-white">
                          {app.status}
                        </Badge>
                        <span className="text-sm text-gray-500">
                          {app.releaseDate}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-3 mb-6">
                      <h4 className="font-semibold text-lg mb-3">
                        Key Features:
                      </h4>
                      {app.features.map((feature, featureIndex) => (
                        <div
                          key={featureIndex}
                          className="flex items-center gap-3"
                        >
                          <Check className="h-5 w-5 text-green-600 flex-shrink-0" />
                          <span className="text-sm text-gray-700">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="text-center">
                      <Button
                        className="w-full bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700"
                        disabled
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Coming Soon
                      </Button>
                      <p className="text-xs text-gray-500 mt-2">
                        Get notified when available
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Card className="inline-block p-6 bg-gradient-to-r from-purple-50 to-teal-50 border-2 border-purple-200">
              <CardContent className="text-center">
                <h3 className="text-xl font-bold mb-2 text-purple-800">
                  Stay Updated
                </h3>
                <p className="text-gray-600 mb-4">
                  Be the first to know when our mobile apps launch!
                </p>
                <Button className="bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700">
                  <Mail className="h-4 w-4 mr-2" />
                  Notify Me
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-20 px-4 bg-gradient-to-br from-purple-50 to-teal-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
              Frequently Asked Questions
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Quick answers to the most common questions
            </p>
          </div>

          <div className="space-y-8">
            {faqData.map((category, categoryIndex) => (
              <motion.div
                key={categoryIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
              >
                <Card>
                  <CardContent className="p-0">
                    <div className="p-6 border-b border-gray-100">
                      <h3 className="text-xl font-bold text-purple-800">
                        {category.category}
                      </h3>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {category.questions.map((faq, questionIndex) => (
                        <div key={questionIndex}>
                          <button
                            className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                            onClick={() =>
                              toggleFaq(categoryIndex, questionIndex)
                            }
                          >
                            <span className="font-medium text-gray-900 pr-4">
                              {faq.question}
                            </span>
                            {expandedFaq?.category === categoryIndex &&
                            expandedFaq?.question === questionIndex ? (
                              <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                            )}
                          </button>
                          <AnimatePresence>
                            {expandedFaq?.category === categoryIndex &&
                              expandedFaq?.question === questionIndex && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: "auto", opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.3 }}
                                  className="overflow-hidden"
                                >
                                  <div className="px-6 pb-4 text-gray-700 leading-relaxed">
                                    {faq.answer}
                                  </div>
                                </motion.div>
                              )}
                          </AnimatePresence>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-12 sm:py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
              Still Need Help?
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Our support team is here to help you get the most out of{" "}
              <span className="text-snuggle-gradient">SnuggleCrafters.com</span>
            </p>
          </div>

          <div className="max-w-md mx-auto">
            {contactOptions.map((option, index) => {
              const Icon = option.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                    <CardContent className="p-6 text-center">
                      <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Icon className="h-8 w-8 text-white" />
                      </div>
                      <h3 className="font-semibold text-xl mb-2">
                        {option.title}
                      </h3>
                      <p className="text-gray-600 mb-4">{option.description}</p>
                      <p className="text-sm text-gray-500 mb-4">
                        {option.availability}
                      </p>
                      <Link href={option.href}>
                        <Button className="w-full" data-testid="button-email-support">
                          {option.action}
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
